import { useEffect, useState } from "react";
import api from "../services/api.tsx";
import ProductCard from "../components/ProductCard.tsx";
import { useAuth } from "../contexts/AuthContext.tsx";
import { useCart } from "../contexts/CartContext.tsx";
import { useError } from "../contexts/ErrorContext.tsx";
import { useSuccess } from "../contexts/SuccessContext.tsx";
import { getDiscountedPrice } from "../utils/price.tsx";
import CampaignCarousel from "../components/CampaignCarousel.tsx";
import Campaign from "../Entities/Campaign.tsx";
import Product from "../Entities/Product.tsx";

const CampaignProductsPage = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const { setError } = useError();
  const { addSuccess } = useSuccess();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [campaignRes, productRes] = await Promise.all([
          api.get("/api/campaign"),
          api.get("/api/product"),
        ]);
        setCampaigns(campaignRes.data);
        setProducts(productRes.data);
      } catch (error) {
        setError("Veriler alınamadı.");
      }
    };
    fetchData();
  }, [setError]);

  // Aktif kampanyalar
  const now = new Date();
  const activeCampaigns = campaigns.filter(
    (c) =>
      c.isActive &&
      new Date(c.startDate) <= now &&
      new Date(c.endDate) >= now
  );

  // Kampanyalı ürünler
  const campaignProductIds = activeCampaigns.flatMap((c) => c.productIds);
  const campaignProducts = products.filter((p) => campaignProductIds.includes(p.id));

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-2 md:px-8 py-6">
        <CampaignCarousel campaigns={activeCampaigns} />
        <h2 className="text-2xl font-bold mb-6 text-center text-blue-700">Aktif Kampanyalı Ürünler</h2>
        {campaignProducts.length === 0 ? (
          <div className="text-center text-gray-500 mt-12 text-lg font-semibold">
            Aktif kampanya bulunamadı.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {campaignProducts.map((product) => {
              const campaign = activeCampaigns.find((c) => c.productIds.includes(product.id));
              const discountedPrice = campaign ? getDiscountedPrice(product.price, campaign.discountRate) : undefined;
              return (
                <ProductCard
                  key={product.id}
                  id={product.id}
                  name={product.name}
                  imageUrl={product.imageUrl}
                  price={product.price}
                  discountRate={campaign?.discountRate}
                  campaignName={
                    campaign
                      ? `%${(campaign.discountRate).toFixed(0)} indirim • Son: ${new Date(campaign.endDate).toLocaleDateString("tr-TR")}`
                      : undefined
                  }
                  campaignEndDate={campaign?.endDate}
                  brand={product.brand}
                  category={product.category}
                  participantCount={campaign?.participantCount}
                  maxUserCount={campaign?.maxUserCount}
                  isAuthenticated={isAuthenticated}
                  hasCampaign={!!campaign}
                  stock={product.stock}
                  onAddToCart={async (quantity) => {
                    try {
                      const response = await addToCart({
                        id: product.id,
                        productId: product.id,
                        name: product.name,
                        price: product.price,
                        stock: product.stock,
                        finalPrice: discountedPrice || product.price,
                        quantity,
                        ...(campaign && { campaignId: campaign.id }),
                        depositRate: campaign?.depositRate || 0
                      });
                      if (response?.success || response?.status === 200) {
                        addSuccess("Ürün sepete eklendi!");
                      }
                    } catch (err) {
                      setError("Ürün sepete eklenemedi!");
                    }
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignProductsPage;